/**
 * MentionTextarea
 *
 * Textarea con autocompletado de @menciones.
 *
 * Estrategia:
 *  - El textarea muestra texto limpio: "@Nombre Apellido" (sin IDs).
 *  - Internamente se mantiene un mapa nombre→id para reconstruir el formato
 *    completo "@[Nombre Apellido](id)" que necesita el backend.
 *  - onChange recibe siempre el valor en formato completo (con IDs).
 *  - value recibido desde el padre también es el formato completo.
 *
 * Props:
 *  - value        {string}   Valor en formato @[Nombre](id) (controlado por el padre)
 *  - onChange     {fn}       Llamado con el nuevo valor en formato @[Nombre](id)
 *  - placeholder  {string}
 *  - rows         {number}   Filas del textarea (default 3)
 *  - className    {string}   Clase CSS extra para el textarea
 *  - style        {object}   Estilos inline extra para el textarea
 *  - onKeyDown    {fn}       Handler extra de teclas
 *  - disabled     {boolean}
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { personasAPI } from '../services/api';

// ─── Helpers de formato ──────────────────────────────────────────────────────

const TRIGGER_REGEX = /@([^\s@]*)$/;
const STORED_RE = /@\[([^\]]+)\]\((\d+)\)/g;

/** @[Nombre](123) → @Nombre  (para mostrar en el textarea) */
const toDisplay = (stored) =>
  (stored || '').replace(/@\[([^\]]+)\]\(\d+\)/g, '@$1');

/** Extrae mapa { 'Nombre Apellido': id } de un valor almacenado */
const parseMentionsMap = (stored) => {
  const map = {};
  let m;
  const re = new RegExp(STORED_RE.source, 'g');
  while ((m = re.exec(stored || '')) !== null) {
    map[m[1]] = parseInt(m[2], 10);
  }
  return map;
};

/**
 * Reconstruye el valor con IDs a partir del texto de display y el mapa.
 * Solo reemplaza las menciones que están en el mapa.
 */
const toStored = (display, mentionsMap) => {
  let result = display;
  for (const [name, id] of Object.entries(mentionsMap)) {
    result = result.split(`@${name}`).join(`@[${name}](${id})`);
  }
  return result;
};

// ─── Exports públicos ────────────────────────────────────────────────────────

/** Formato de mención almacenada (para uso externo si hace falta) */
export const formatMention = (persona) =>
  `@[${persona.nombre} ${persona.apellido}](${persona.id})`;

/**
 * Renderiza un texto con menciones almacenadas (@[Nombre](id)) como JSX,
 * mostrando las menciones en azul y negrita.
 * Usar en la visualización de comentarios guardados.
 */
export const renderMentionText = (texto) => {
  if (!texto) return null;
  const parts = [];
  let lastIndex = 0;
  const re = new RegExp(STORED_RE.source, 'g');
  let match;
  while ((match = re.exec(texto)) !== null) {
    if (match.index > lastIndex) {
      parts.push(texto.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={match.index} style={{ color: '#1a73e8', fontWeight: '700' }}>
        @{match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < texto.length) {
    parts.push(texto.slice(lastIndex));
  }
  return parts.length > 0 ? parts : texto;
};

// ─── Componente ──────────────────────────────────────────────────────────────

const MentionTextarea = ({
  value,
  onChange,
  placeholder = 'Escriba su comentario aquí... Use @ para mencionar a alguien',
  rows = 3,
  className = '',
  style = {},
  onKeyDown,
  disabled = false,
}) => {
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  // Mapa nombre→id de las menciones conocidas en el texto actual
  const mentionsMapRef = useRef(parseMentionsMap(value));

  // El textarea muestra texto limpio (sin IDs)
  const [displayValue, setDisplayValue] = useState(() => toDisplay(value));
  const [personas, setPersonas] = useState([]);
  const [dropdown, setDropdown] = useState(null);

  // Sincronizar display cuando el padre resetea el value (p.ej. tras enviar)
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setDisplayValue(toDisplay(value));
      mentionsMapRef.current = parseMentionsMap(value);
    }
  }, [value]);

  // Cargar personas activas una sola vez
  useEffect(() => {
    personasAPI.getAll({ activo: true })
      .then((res) => setPersonas(res.data?.results ?? res.data ?? []))
      .catch(() => {});
  }, []);

  // Filtrar personas por la query actual
  const buscarPersonas = useCallback(
    (query) => {
      const q = query.toLowerCase();
      return personas
        .filter((p) => `${p.nombre} ${p.apellido}`.toLowerCase().includes(q))
        .slice(0, 8);
    },
    [personas]
  );

  const handleChange = (e) => {
    const newDisplay = e.target.value;
    setDisplayValue(newDisplay);
    onChange(toStored(newDisplay, mentionsMapRef.current));

    // Detectar trigger "@..."
    const cursor = e.target.selectionStart;
    const textUpToCursor = newDisplay.slice(0, cursor);
    const match = TRIGGER_REGEX.exec(textUpToCursor);
    if (match) {
      const opciones = buscarPersonas(match[1]);
      setDropdown({ query: match[1], opciones, selectedIdx: 0, cursorPos: cursor });
    } else {
      setDropdown(null);
    }
  };

  const insertarMencion = useCallback(
    (persona) => {
      if (!dropdown) return;
      const ta = textareaRef.current;
      const cursor = dropdown.cursorPos;
      const textUpToCursor = displayValue.slice(0, cursor);
      const match = TRIGGER_REGEX.exec(textUpToCursor);
      if (!match) return;

      const startOfTrigger = cursor - match[0].length;
      const before = displayValue.slice(0, startOfTrigger);
      const after = displayValue.slice(cursor);
      const nombre = `${persona.nombre} ${persona.apellido}`;
      const displayMention = `@${nombre}`;
      const newDisplay = `${before}${displayMention} ${after}`;

      // Registrar mención en el mapa
      mentionsMapRef.current[nombre] = persona.id;
      setDisplayValue(newDisplay);
      onChange(toStored(newDisplay, mentionsMapRef.current));
      setDropdown(null);

      // Reposicionar cursor tras la mención
      requestAnimationFrame(() => {
        const newCursor = startOfTrigger + displayMention.length + 1;
        ta.focus();
        ta.setSelectionRange(newCursor, newCursor);
      });
    },
    [dropdown, displayValue, onChange]
  );

  const handleKeyDown = (e) => {
    if (dropdown && dropdown.opciones.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDropdown((d) => ({ ...d, selectedIdx: Math.min(d.selectedIdx + 1, d.opciones.length - 1) }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDropdown((d) => ({ ...d, selectedIdx: Math.max(d.selectedIdx - 1, 0) }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertarMencion(dropdown.opciones[dropdown.selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setDropdown(null);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        e.target !== textareaRef.current
      ) {
        setDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showDropdown = dropdown && dropdown.opciones.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Textarea: muestra texto limpio sin IDs */}
      <textarea
        ref={textareaRef}
        className={className}
        rows={rows}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
      />
      {showDropdown && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            zIndex: 9999,
            top: '100%',
            left: 0,
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '220px',
            maxWidth: '360px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              color: '#6c757d',
              borderBottom: '1px solid #f1f3f4',
              background: '#f8f9fa',
            }}
          >
            Mencionar persona
          </div>
          {dropdown.opciones.map((p, idx) => (
            <div
              key={p.id}
              onMouseDown={(e) => {
                e.preventDefault(); // evitar que el textarea pierda el foco
                insertarMencion(p);
              }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                backgroundColor: idx === dropdown.selectedIdx ? '#e8f0fe' : 'transparent',
                borderBottom: idx < dropdown.opciones.length - 1 ? '1px solid #f1f3f4' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={() =>
                setDropdown((d) => ({ ...d, selectedIdx: idx }))
              }
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#4a90d9',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '12px',
                  flexShrink: 0,
                }}
              >
                {p.nombre.charAt(0).toUpperCase()}
              </span>
              <div>
                <div style={{ fontWeight: '500', color: '#2c3e50' }}>
                  {p.nombre} {p.apellido}
                </div>
                {p.email && (
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>{p.email}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
